<template>
	<div class="modal" tabindex="-1" role="dialog" id="muven-channel-channel-creation-modal">
		<div class="modal-dialog modal-lg" role="document">
			<div class="modal-content modal-">
				<div class="modal-header">
					<h5 class="modal-title">Novo canal de integração</h5>
				</div>
				<div class="modal-body">
					<div class="row">
						<div class="col-12">
							<div class="muven-tip tip-info mb-3">
								<fa icon="info" class="tip-icon" />
								<p>Apenas adicione canais que realmente deseja e irá utilizar. Canais adicionados e não
									utilizados serão removidos.</p>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="form-group col-12">
							<div class="channel-creation-label">
								<label>Novo canal</label>
								<button type="button" class="channel-kind-toggle" :aria-expanded="showKindComparison ? 'true' : 'false'"
									@click="showKindComparison = !showKindComparison">
									<fa icon="question-circle" />
									Marketplace ou e-commerce? Qual a diferença
									<fa icon="chevron-down" class="channel-kind-toggle-chevron" :class="{ 'is-open': showKindComparison }" />
								</button>
							</div>

							<transition name="channel-kind-reveal">
								<div v-if="showKindComparison" class="channel-kind-comparison">
									<article v-for="card in kindComparison" :key="card.kind" class="channel-kind-card"
										:class="`is-${card.kind}`">
										<header class="channel-kind-card-header">
											<span class="channel-kind-card-art" aria-hidden="true">
												<!-- Shopping: um prédio com várias lojas lado a lado -->
												<svg v-if="card.kind === 'marketplace'" viewBox="0 0 48 48" fill="none"
													stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
													<path d="M6 18 L24 7 L42 18" />
													<rect x="8" y="18" width="32" height="23" rx="1.5" />
													<path d="M8 26 h32" />
													<rect x="12" y="30" width="6" height="11" />
													<rect x="21" y="30" width="6" height="11" />
													<rect x="30" y="30" width="6" height="11" />
													<path d="M13 22 h4 M22 22 h4 M31 22 h4" />
												</svg>
												<!-- Loja de rua: fachada única com toldo -->
												<svg v-else viewBox="0 0 48 48" fill="none"
													stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
													<path d="M7 17 L10 9 H38 L41 17" />
													<path d="M7 17 q4.25 5 8.5 0 q4.25 5 8.5 0 q4.25 5 8.5 0 q4.25 5 8.5 0" />
													<path d="M10 21 V41 H38 V21" />
													<rect x="14" y="26" width="9" height="15" />
													<rect x="27" y="26" width="7" height="7" rx="1" />
													<path d="M4 41 h40" />
												</svg>
											</span>
											<div>
												<p class="channel-kind-card-eyebrow">{{ card.name }}</p>
												<p class="channel-kind-card-analogy">{{ card.analogy }}</p>
											</div>
										</header>

										<dl class="channel-kind-card-facts">
											<div v-for="fact in card.facts" :key="fact.label">
												<dt>{{ fact.label }}</dt>
												<dd>{{ fact.value }}</dd>
											</div>
										</dl>

										<footer class="channel-kind-card-examples">
											<span class="channel-kind-card-examples-label">Exemplos</span>
											<span class="channel-kind-card-logos">
												<ChannelLogoCmp v-for="example in card.examples" :key="example.enumChannel"
													:channel="example" :size="24" :show-name="false" />
											</span>
										</footer>
									</article>

									<p class="channel-kind-comparison-note">
										Não é um ou outro: muitas lojas vendem nos dois ao mesmo tempo, e o Muven integra ambos.
									</p>
								</div>
							</transition>

							<multiselect v-model="channelCreation.channel" :options="channelOptionGroups"
								group-values="channels" group-label="label" :group-select="false"
								:multiple="false" :show-labels="false" :allow-empty="true" label="name"
								track-by="enumChannel" placeholder="" :max-height="380" class="channel-creation-select">
								<template slot="option" slot-scope="{ option }">
									<div v-if="option.$isLabel" class="channel-option-group">
										<div class="channel-option-group-title">
											<span>{{ option.$groupLabel }}</span>
											<span class="channel-option-group-count">{{ groupCount(option.$groupLabel) }}</span>
										</div>
										<p class="channel-option-group-hint">{{ groupHint(option.$groupLabel) }}</p>
									</div>
									<div v-else-if="option.isComingSoon" class="channel-option-soon">
										<ChannelLogoCmp :channel="option" :size="20" muted />
										<span class="channel-option-soon-badge">Em breve</span>
									</div>
									<ChannelLogoCmp v-else :channel="option" :size="20" :muted="option.isUnavailable">
										<span :class="{ 'shopify-unavailable': option.isUnavailable }">{{ option.name
										}}<template v-if="option.isUnavailable"> (não recomendado)</template></span>
									</ChannelLogoCmp>
								</template>
								<template slot="singleLabel" slot-scope="{ option }">
									<ChannelLogoCmp :channel="option" :size="20" :muted="option.isUnavailable" />
								</template>
							</multiselect>

							<!-- Explica, com o canal escolhido, o que a integração vai fazer -->
							<div v-if="selectedKindExplanation" class="channel-kind-explanation">
								<ChannelLogoCmp :channel="channelCreation.channel" :size="28" :show-name="false" />
								<p>
									<strong>{{ channelCreation.channel.name }} {{ selectedKindExplanation.title }}.</strong>
									{{ selectedKindExplanation.text }}
								</p>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="form-group col-12">
							<label>Digite "CONFIRMO" para continuar</label>
							<input type="text" class="form-control" v-model="channelCreation.confirmationMessage">
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-primary"
						:disabled="submittingChannelCreation || !channelCreation.isValid()"
						@click="createChannel(channelCreation)">
						Adicionar canal
						<Loading v-show="submittingChannelCreation" />
					</button>

					<button type="button" class="btn btn-outline-secondary" @click="closeModal()">Fechar</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { ChannelEnum, getChannelEnumDescription } from '@c2b/muven-commons'
import modalService from '../../utils/services/ModalService'
import channelService from '../services/ChannelService'
import { ChannelCreationModel } from '../models/ChannelCreationModel'
import subscriberManager from '@/subscriber/managers/SubscriberManager'
import ChannelLogoCmp from './ChannelLogoCmp.vue'

const ID_CHANNEL_CREATION_MODAL = '#muven-channel-channel-creation-modal'

const MARKETPLACE_CHANNELS = [
	ChannelEnum.MercadoLivre,
	ChannelEnum.Shopee,
	ChannelEnum.MagazineLuiza,
	ChannelEnum.B2W,
	ChannelEnum.Shein,
	ChannelEnum.TiktokShop,
]

// Marketplaces exibidos como próximos lançamentos: aparecem na lista, mas não
// podem ser selecionados ($isDisabled é tratado pelo vue-multiselect).
const COMING_SOON_MARKETPLACES = [
	{ enumChannel: ChannelEnum.Amazon, name: getChannelEnumDescription(ChannelEnum.Amazon) },
	{ enumChannel: ChannelEnum.TiktokShop, name: 'TikTok Shop' },
].map((c) => ({ ...c, isComingSoon: true, $isDisabled: true }))

// Analogia shopping x loja de rua: quem não é do varejo costuma confundir os dois.
const CHANNEL_KINDS = {
	marketplace: {
		groupLabel: 'Marketplaces',
		hint: 'Vitrines de outras empresas, como um shopping. Você anuncia, a plataforma traz o público.',
		title: 'é um marketplace',
		text: 'O Muven vai publicar seus produtos como anúncios dentro dele e trazer de volta os pedidos feitos lá.',
	},
	ecommerce: {
		groupLabel: 'E-commerces',
		hint: 'Sua própria loja virtual, como uma loja de rua. Sua marca, seu site.',
		title: 'é uma plataforma de e-commerce',
		text: 'O Muven vai manter produtos, estoque e preços da sua loja virtual sincronizados com o seu ERP.',
	},
	custom: {
		groupLabel: 'Outros',
		hint: 'Integrações sob medida, via API.',
	},
}

const exampleOf = (enumChannel) => ({ enumChannel, name: getChannelEnumDescription(enumChannel) })

// Comparativo do "Qual a diferença": um cartão por modelo, na mesma ordem dos grupos.
const KIND_COMPARISON = [
	{
		kind: 'marketplace',
		name: 'Marketplace',
		analogy: 'É como uma loja dentro do shopping',
		facts: [
			{ label: 'Quem traz o cliente', value: 'O marketplace' },
			{ label: 'Marca e endereço', value: 'Do marketplace' },
			{ label: 'Custo típico', value: 'Comissão por venda' },
		],
		examples: [ChannelEnum.MercadoLivre, ChannelEnum.Shopee, ChannelEnum.MagazineLuiza].map(exampleOf),
	},
	{
		kind: 'ecommerce',
		name: 'E-commerce',
		analogy: 'É como uma loja de rua, só sua',
		facts: [
			{ label: 'Quem traz o cliente', value: 'Você (anúncios, redes sociais, SEO)' },
			{ label: 'Marca e endereço', value: 'Seus (ex.: www.sualoja.com.br)' },
			{ label: 'Custo típico', value: 'Mensalidade da plataforma' },
		],
		examples: [ChannelEnum.WBuy, ChannelEnum.Tray, ChannelEnum.VTex].map(exampleOf),
	},
]

// Mercado Livre e Shopee não recebem produtos publicados pelo Muven: o anúncio
// nasce no canal e é associado manualmente (mesmo aviso do site muven.com.br).
const MANUAL_ASSOCIATION_TEXT = 'Nele, o anúncio é criado direto no canal e depois associado manualmente ao produto no Muven. ' +
	'A partir daí, o Muven passa a acompanhar esse anúncio e trazer os pedidos feitos lá.'

const CHANNEL_EXPLANATION_OVERRIDES = {
	[ChannelEnum.MercadoLivre]: MANUAL_ASSOCIATION_TEXT,
	[ChannelEnum.Shopee]: MANUAL_ASSOCIATION_TEXT,
}

export default {
	name: 'ChannelCreationCmp',
	components: {
		ChannelLogoCmp,
	},
	data() {

		const channelOptionList = [
			{ enumChannel: ChannelEnum.WBuy, name: getChannelEnumDescription(ChannelEnum.WBuy) },
			{ enumChannel: ChannelEnum.MercadoLivre, name: getChannelEnumDescription(ChannelEnum.MercadoLivre) },
			{ enumChannel: ChannelEnum.Shopee, name: getChannelEnumDescription(ChannelEnum.Shopee) },
			{ enumChannel: ChannelEnum.MagazineLuiza, name: getChannelEnumDescription(ChannelEnum.MagazineLuiza) },
			{ enumChannel: ChannelEnum.B2W, name: getChannelEnumDescription(ChannelEnum.B2W) },
			{ enumChannel: ChannelEnum.Shein, name: getChannelEnumDescription(ChannelEnum.Shein) },
			{ enumChannel: ChannelEnum.Tray, name: getChannelEnumDescription(ChannelEnum.Tray) },
			{ enumChannel: ChannelEnum.LojaIntegrada, name: getChannelEnumDescription(ChannelEnum.LojaIntegrada) },
			{ enumChannel: ChannelEnum.WooCommerce, name: getChannelEnumDescription(ChannelEnum.WooCommerce) },
			{ enumChannel: ChannelEnum.NuvemShop, name: getChannelEnumDescription(ChannelEnum.NuvemShop) },
			{ enumChannel: ChannelEnum.VTex, name: getChannelEnumDescription(ChannelEnum.VTex) },
			{ enumChannel: ChannelEnum.Shopify, name: getChannelEnumDescription(ChannelEnum.Shopify) },
			{ enumChannel: ChannelEnum.Flexy, name: getChannelEnumDescription(ChannelEnum.Flexy) },
			{ enumChannel: ChannelEnum.Magento_1_9, name: getChannelEnumDescription(ChannelEnum.Magento_1_9) },
			{ enumChannel: ChannelEnum.Custom, name: getChannelEnumDescription(ChannelEnum.Custom) },

		];

		const publicKey = subscriberManager.getPublicKeyFromStorage();
		const isC2bHml = publicKey === 'rafael-c2b-hml';

		if (!isC2bHml) {

			const shopifyOption = channelOptionList.find(
				(c) => c.enumChannel === ChannelEnum.Shopify
			);

			if (shopifyOption) { shopifyOption.isUnavailable = true; }
		} else {
			// TikTok Shop liberado só para a conta de homologação
			channelOptionList.push({ enumChannel: ChannelEnum.TiktokShop, name: 'TikTok Shop' });
		}

		const isAvailable = (c) => channelOptionList.some((o) => o.enumChannel === c.enumChannel);

		const byName = (a, b) => a.name.localeCompare(b.name);
		const isMarketplace = (c) => MARKETPLACE_CHANNELS.includes(c.enumChannel);
		const isCustom = (c) => c.enumChannel === ChannelEnum.Custom;
		const isWBuy = (c) => c.enumChannel === ChannelEnum.WBuy;

		const marketplaces = [
			...channelOptionList.filter(isMarketplace).sort(byName),
			...COMING_SOON_MARKETPLACES.filter((c) => !isAvailable(c)),
		];
		// WBuy segue no topo das plataformas de e-commerce
		const ecommerces = channelOptionList
			.filter((c) => !isMarketplace(c) && !isCustom(c))
			.sort((a, b) => (isWBuy(b) - isWBuy(a)) || byName(a, b));
		const others = channelOptionList.filter(isCustom);

		const groupOf = (kind, channels) => ({
			kind,
			label: CHANNEL_KINDS[kind].groupLabel,
			hint: CHANNEL_KINDS[kind].hint,
			channels: channels.map((c) => ({ ...c, kind })),
		});

		const channelOptionGroups = [
			groupOf('marketplace', marketplaces),
			groupOf('ecommerce', ecommerces),
			groupOf('custom', others),
		].filter((g) => g.channels.length);

		return {
			submittingChannelCreation: false,
			showKindComparison: false,
			kindComparison: KIND_COMPARISON,
			channelCreation: new ChannelCreationModel(),
			channelOptionGroups
		}
	},
	computed: {
		selectedKindExplanation() {
			const channel = this.channelCreation.channel;
			const kind = channel && CHANNEL_KINDS[channel.kind];
			if (!kind || !kind.text) {
				return null;
			}

			const text = CHANNEL_EXPLANATION_OVERRIDES[channel.enumChannel] || kind.text;
			return { ...kind, text };
		},
	},
	methods: {
		findGroup(label) {
			return this.channelOptionGroups.find((g) => g.label === label);
		},
		groupCount(label) {
			const group = this.findGroup(label);
			return group ? group.channels.filter((c) => !c.isComingSoon).length : 0;
		},
		groupHint(label) {
			const group = this.findGroup(label);
			return group ? group.hint : '';
		},
		async createChannel(channelCreation) {
			this.submittingChannelCreation = true

			try {
				const c = await channelService.createChannel(channelCreation)
				this.closeModal()

				this.$emit('channelCreated');

				this.$floatingAlert.success({
					title: '',
					message: `Canal ${c.name} adicionado com sucesso.`
				})
			} catch (err) {
				this.$floatingAlert.error({
					title: 'Oops!',
					message: err.message || 'Houve um problema ao tentar criar seu pedido de teste. Tente novamente mais tarde.'
				})
			}

			this.submittingChannelCreation = false
		},
		openModal() {
			this.channelCreation = new ChannelCreationModel()
			modalService.show(ID_CHANNEL_CREATION_MODAL)
		},
		closeModal() {
			modalService.hide(ID_CHANNEL_CREATION_MODAL)
		},
	},
}
</script>

<style scoped>
.shopify-unavailable {
	color: var(--danger-fg);
	font-style: italic;
}

.channel-creation-select ::v-deep .multiselect__option {
	padding-left: 1.5rem;
}

.channel-creation-select ::v-deep .multiselect__option--group,
.channel-creation-select ::v-deep .multiselect__option--group.multiselect__option--highlight {
	padding: .5rem .75rem .35rem;
	min-height: 0;
	background: var(--surface-2);
	color: var(--text-muted);
	cursor: default;
}

.channel-creation-select ::v-deep li:not(:first-child) > .multiselect__option--group {
	border-top: 1px solid var(--border-subtle);
}

/* Opções "em breve" também recebem --group/--disabled do multiselect; voltam ao
   visual de opção comum, só que apagadas. */
.channel-creation-select ::v-deep .multiselect__option--disabled:has(.channel-option-soon) {
	padding: 12px 12px 12px 1.5rem;
	min-height: 40px;
	background: var(--surface-1) !important;
	color: var(--text-muted) !important;
	border-top: 0 !important;
}

.channel-option-soon {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: .5rem;
	opacity: .75;
}

.channel-option-soon-badge {
	padding: .1rem .5rem;
	border: 1px dashed var(--border-strong);
	border-radius: 999px;
	color: var(--text-muted);
	font-size: .65rem;
	font-weight: 600;
	letter-spacing: .06em;
	text-transform: uppercase;
	white-space: nowrap;
}

.channel-option-group-title {
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-size: .7rem;
	font-weight: 600;
	letter-spacing: .08em;
	text-transform: uppercase;
	line-height: 1.2;
}

.channel-option-group-hint {
	margin: .2rem 0 0;
	color: var(--text-muted);
	font-size: .75rem;
	line-height: 1.3;
	white-space: normal;
}

.channel-creation-label {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: .25rem 1rem;
}

.channel-kind-toggle {
	padding: 0;
	border: 0;
	background: none;
	color: var(--text-muted);
	font-size: .8rem;
	cursor: pointer;
}

.channel-kind-toggle:hover {
	color: var(--text);
	text-decoration: underline;
}

.channel-kind-toggle-chevron {
	margin-left: .15rem;
	font-size: .7em;
	transition: transform .15s ease;
}

.channel-kind-toggle-chevron.is-open {
	transform: rotate(180deg);
}

/* Comparativo: dois cartões lado a lado, cada modelo com sua cor de destaque.
   Laranja = shopping (movimento, vitrine alheia); roxo Muven = a loja do cliente. */
.channel-kind-comparison {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: .75rem;
	margin-bottom: .9rem;
}

.channel-kind-card {
	--kind-accent: var(--accent-orange);
	--kind-tint: var(--warning-bg);

	display: flex;
	flex-direction: column;
	border: 1px solid var(--border-subtle);
	border-radius: 10px;
	background: var(--surface-1);
	box-shadow: 0 1px 2px color-mix(in srgb, var(--shadow-ink) 4%, transparent);
	overflow: hidden;
	transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;
}

.channel-kind-card.is-ecommerce {
	--kind-accent: var(--brand);
	--kind-tint: var(--tint-brand-hover);
}

.channel-kind-card:hover {
	border-color: var(--kind-accent);
	box-shadow: 0 6px 16px -8px color-mix(in srgb, var(--shadow-ink) 18%, transparent);
	transform: translateY(-1px);
}

.channel-kind-card-header {
	display: flex;
	align-items: center;
	gap: .75rem;
	padding: .85rem .9rem;
	background: linear-gradient(135deg, var(--kind-tint) 0%, var(--surface-1) 85%);
	border-bottom: 1px solid var(--border-subtle);
}

.channel-kind-card-art {
	flex: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border-radius: 10px;
	background: var(--surface-1);
	color: var(--kind-accent);
	box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--shadow-ink) 4%, transparent), 0 1px 2px color-mix(in srgb, var(--shadow-ink) 6%, transparent);
}

.channel-kind-card-art svg {
	width: 30px;
	height: 30px;
}

.channel-kind-card-eyebrow {
	margin: 0;
	color: var(--kind-accent);
	font-size: .68rem;
	font-weight: 700;
	letter-spacing: .1em;
	text-transform: uppercase;
}

.channel-kind-card-analogy {
	margin: .1rem 0 0;
	color: var(--text-strong);
	font-size: .98rem;
	font-weight: 600;
	line-height: 1.25;
}

.channel-kind-card-facts {
	flex: 1;
	margin: 0;
	padding: .35rem .9rem;
}

.channel-kind-card-facts > div {
	padding: .45rem 0;
}

.channel-kind-card-facts > div + div {
	border-top: 1px dashed var(--border-subtle);
}

.channel-kind-card-facts dt {
	color: var(--text-muted);
	font-size: .7rem;
	font-weight: 500;
}

.channel-kind-card-facts dd {
	margin: 0;
	color: var(--text);
	font-size: .84rem;
}

.channel-kind-card-examples {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: .55rem .9rem;
	background: var(--surface-2);
	border-top: 1px solid var(--border-subtle);
}

.channel-kind-card-examples-label {
	color: var(--text-muted);
	font-size: .7rem;
}

.channel-kind-card-logos {
	display: inline-flex;
	gap: .3rem;
}

.channel-kind-comparison-note {
	grid-column: 1 / -1;
	margin: 0;
	color: var(--text-muted);
	font-size: .78rem;
	text-align: center;
}

.channel-kind-reveal-enter-active,
.channel-kind-reveal-leave-active {
	transition: opacity .18s ease, transform .18s ease;
}

.channel-kind-reveal-enter,
.channel-kind-reveal-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}

/* Entrada escalonada: primeiro o shopping, depois a loja de rua */
.channel-kind-reveal-enter-active .channel-kind-card {
	animation: channel-kind-card-in .28s ease both;
}

.channel-kind-reveal-enter-active .channel-kind-card:nth-child(2) {
	animation-delay: .06s;
}

@keyframes channel-kind-card-in {
	from {
		opacity: 0;
		transform: translateY(6px);
	}
}

@media (max-width: 575.98px) {
	.channel-kind-comparison {
		grid-template-columns: 1fr;
	}
}

@media (prefers-reduced-motion: reduce) {
	.channel-kind-card,
	.channel-kind-reveal-enter-active,
	.channel-kind-reveal-leave-active,
	.channel-kind-reveal-enter-active .channel-kind-card {
		transition: none;
		animation: none;
	}
}

.channel-kind-explanation {
	display: flex;
	align-items: flex-start;
	gap: .65rem;
	margin-top: .6rem;
	padding: .6rem .75rem;
	border-radius: 6px;
	background: var(--surface-2);
}

.channel-kind-explanation p {
	margin: 0;
	color: var(--text-soft);
	font-size: .85rem;
	line-height: 1.4;
}

.channel-option-group-count {
	padding: .05rem .45rem;
	border-radius: 999px;
	background: var(--surface-3);
	font-size: .65rem;
	letter-spacing: 0;
}
</style>